// client/src/lib/net.ts
/**
 * The ONE place that knows where the server is and where the login token lives.
 * Web: every function behaves exactly as before (relative URLs, localStorage 'token').
 * App (client-app/): configureNet() runs BEFORE first render with the server origin and a
 * Keychain-backed store whose value was loaded asynchronously at boot — so getToken()
 * stays synchronous for the ~100 existing callers.
 * Spec: docs/superpowers/specs/2026-09-26-mobile-app-design.md §3.
 */
export interface TokenStore { get(): string | null; set(token: string): void; clear(): void; flush?(): Promise<void> }

const localTokenStore: TokenStore = {
  get: () => { try { return localStorage.getItem("token"); } catch { return null; } },
  set: (t) => { try { localStorage.setItem("token", t); } catch { /* private mode */ } },
  clear: () => { try { localStorage.removeItem("token"); } catch { /* ignore */ } },
};

let origin = "";            // "" = same origin (web)
let store: TokenStore = localTokenStore;
let app = false;
/** Opens a URL in the system browser — injected by client-app (it owns @capacitor/browser). */
let externalOpener: ((url: string) => void) | null = null;

/**
 * Server paths that must be fetched from the server when the page is not served by it.
 * `/images/…` is DATA-borne: rows such as the dance programme's group photo store
 * `/images/dsouls-dance.jpg`, a file only the server's public dir has — in the app it resolved to
 * capacitor://localhost and broke (audit C5, 2026-10-01). The server also serves the few bundled
 * /images files, so absolute is correct for both.
 */
const SERVER_PATH = /^\/(api|uploads|objects|public-objects|images)(\/|$|\?)/;

export function configureNet(opts: {
  origin: string; tokenStore: TokenStore; app: boolean; openUrl?: (url: string) => void;
}): void {
  origin = opts.origin.replace(/\/+$/, ""); store = opts.tokenStore; app = opts.app;
  externalOpener = opts.openUrl ?? null;
}
/** Test-only. */
export function resetNet(): void { origin = ""; store = localTokenStore; app = false; externalOpener = null; }

export const isAppBuild = (): boolean => app;
export function apiUrl(path: string): string {
  if (!origin || typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) return path;
  return origin + path;
}
/** Cross-origin requests must NOT carry credentials: appCors never sends Allow-Credentials (Bearer only). */
export const credentialsMode = (): RequestCredentials => (app ? "omit" : "include");
/** For server-served assets that arrive as relative URLs (/uploads/…, /api/…/pdf). */
export function assetUrl(path: string | null | undefined): string {
  if (!path) return "";
  return origin && SERVER_PATH.test(path) ? origin + path : path;
}
export const getToken = (): string | null => store.get();
export const setToken = (t: string): void => store.set(t);
export const clearToken = (): void => store.clear();
/** Await pending secure-storage writes before a full-page navigation (logout, reloads). No-op on the web. */
export const flushToken = (): Promise<void> => store.flush?.() ?? Promise.resolve();

/** Origin to put in links people SHARE (never capacitor://localhost). */
export function publicOrigin(): string { return origin || window.location.origin; }
/** The origin of THIS document (capacitor://localhost in the app) — for same-document link checks, never for sharing. */
export function pageOrigin(): string { return window.location.origin; }
export function wsOrigin(): string {
  const o = publicOrigin();
  return o.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
}
/**
 * Off-app URLs: the system browser in the app, a new tab on the web.
 * The app's opener is INJECTED via configureNet({ openUrl }) from client-app/src/main.tsx, which
 * owns the @capacitor/browser dependency — so the web bundle and the root package.json never
 * reference it (a dynamic import here would still have to resolve at web build time).
 * Relative server paths (/api/…/pdf, /uploads/…) are made absolute: the system browser has no
 * page origin to resolve them against.
 */
export function openExternal(url: string, ...webArgs: [target?: string, features?: string]): Window | null {
  if (app) {
    // A relative page path (/help, /live-photos/slideshow/…) is not a server asset, but the system
    // browser still has no page origin to resolve it against: it is the public site's page.
    const abs = assetUrl(url) || url;
    const full = abs.startsWith("/") && !abs.startsWith("//") && origin ? origin + abs : abs;
    if (externalOpener) { try { externalOpener(full); return null; } catch { /* fall through */ } }
    window.location.href = full;
    return null;
  }
  // Web: the caller's exact window.open arguments (byte-identical to before), or a
  // detached new tab when none are given.
  return webArgs.length ? window.open(url, ...webArgs) : window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * onClick for an <a target="_blank"> that points off-app (PDFs, receipts, Zoho pages).
 * Web: does nothing — the link behaves exactly as before. App: the system browser
 * (a WebView cannot open a new tab, and a relative href would hit capacitor://localhost).
 */
export function externalLinkClick(url: string): (e: { preventDefault(): void }) => void {
  return (e) => { if (app) { e.preventDefault(); openExternal(url); } };
}

/**
 * assetUrl for a render site (`<img src>`, `<video src>`, download `href`): an empty/absent
 * value stays exactly what it was (so React still omits the attribute), anything else goes
 * through assetUrl — unchanged on the web, server-origin-prefixed for /uploads, /api, … in
 * the app. Idempotent (absolute URLs pass through), so wrapping twice is harmless.
 */
export function assetSrc<T extends string | null | undefined>(url: T): T {
  return (url ? assetUrl(url) : url) as T;
}

/**
 * A lamirage.co PAGE (not an API call) to open off-app, e.g. the public website preview.
 * Web: the path unchanged (same tab/new tab as before). App: absolute on the server origin —
 * a relative path would load capacitor://localhost/… i.e. the app's own bundle.
 */
export const siteUrl = (path: string): string => apiUrl(path);

/**
 * The scope claim of a stored JWT, read WITHOUT verification (the server verifies; this only picks
 * the client path). 'crew' = a crew-scoped app token (server/lib/crewToken.ts): it is refreshed via
 * /api/auth/crew/refresh, never /api/auth/refresh-token, and the client profile endpoint must not be
 * called with it (every main-secret verifier rejects it). Mobile app v1, Task C4.
 */
export function tokenScope(token: string | null | undefined = getToken()): "crew" | null {
  if (!token || typeof token !== "string") return null;
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)));
    return json && (json.scope === "crew" || json.typ === "crew") ? "crew" : null;
  } catch {
    return null;
  }
}
